import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';
import { 
  TrendingUp, 
  TrendingDown,
  IndianRupee,
  Package,
  Users,
  Store,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  Activity,
  ShoppingCart,
  Target,
  Zap,
  BarChart3,
  PieChart,
  Layers,
  AlertCircle,
  ChevronRight,
  Sparkles,
  Upload,
  MessageSquare,
  LineChart,
  Boxes,
  Brain
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Agent-specific color configurations (enterprise palette)
const AGENT_STYLES = {
  SalesIQ: {
    bg: 'bg-[#EEF3FF]',
    iconBg: 'bg-[#DBEAFE]',
    iconColor: 'text-[#2563EB]',
    hoverBorder: 'hover:border-[#93C5FD]',
    accent: '#2563EB'
  },
  Customer360: {
    bg: 'bg-[#F3EEFF]',
    iconBg: 'bg-[#E9D5FF]',
    iconColor: 'text-[#7C3AED]',
    hoverBorder: 'hover:border-[#C4B5FD]',
    accent: '#7C3AED'
  },
  StorePulse: {
    bg: 'bg-[#E9FAF6]',
    iconBg: 'bg-[#CCFBF1]',
    iconColor: 'text-[#0D9488]',
    hoverBorder: 'hover:border-[#5EEAD4]',
    accent: '#0D9488'
  },
  ProductMix: {
    bg: 'bg-[#FFF3E8]',
    iconBg: 'bg-[#FFEDD5]',
    iconColor: 'text-[#EA580C]',
    hoverBorder: 'hover:border-[#FDBA74]',
    accent: '#EA580C'
  },
  StockSense: {
    bg: 'bg-[#F1F5F9]',
    iconBg: 'bg-[#E2E8F0]',
    iconColor: 'text-[#475569]',
    hoverBorder: 'hover:border-[#94A3B8]',
    accent: '#475569'
  },
  VendorLink: {
    bg: 'bg-[#FDF4FF]',
    iconBg: 'bg-[#FAE8FF]',
    iconColor: 'text-[#A21CAF]',
    hoverBorder: 'hover:border-[#E879F9]',
    accent: '#A21CAF'
  },
  PromoIQ: {
    bg: 'bg-[#FEF3F2]',
    iconBg: 'bg-[#FEE2E2]',
    iconColor: 'text-[#DC2626]',
    hoverBorder: 'hover:border-[#FCA5A5]',
    accent: '#DC2626'
  },
  DemandFlow: {
    bg: 'bg-[#F0FDFA]',
    iconBg: 'bg-[#CCFBF1]',
    iconColor: 'text-[#0891B2]',
    hoverBorder: 'hover:border-[#67E8F9]',
    accent: '#0891B2'
  }
};

// Quick action configurations
const QUICK_ACTION_STYLES = {
  upload: {
    bg: 'bg-slate-50',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
    hoverBg: 'hover:bg-slate-100',
    hoverBorder: 'hover:border-slate-300',
    hoverShadow: 'hover:shadow-[0_0_0_2px_rgba(100,116,139,0.1)]'
  },
  agents: {
    bg: 'bg-[#EEF3FF]',
    iconBg: 'bg-[#DBEAFE]',
    iconColor: 'text-[#2563EB]',
    hoverBg: 'hover:bg-[#DBEAFE]',
    hoverBorder: 'hover:border-[#93C5FD]',
    hoverShadow: 'hover:shadow-[0_0_0_2px_rgba(37,99,235,0.1)]'
  },
  brain: {
    bg: 'bg-[#F3EEFF]',
    iconBg: 'bg-[#E9D5FF]',
    iconColor: 'text-[#7C3AED]',
    hoverBg: 'hover:bg-[#E9D5FF]',
    hoverBorder: 'hover:border-[#C4B5FD]',
    hoverShadow: 'hover:shadow-[0_0_0_2px_rgba(124,58,237,0.1)]'
  },
  chat: {
    bg: 'bg-[#E9FAF6]',
    iconBg: 'bg-[#CCFBF1]',
    iconColor: 'text-[#0D9488]',
    hoverBg: 'hover:bg-[#CCFBF1]',
    hoverBorder: 'hover:border-[#5EEAD4]',
    hoverShadow: 'hover:shadow-[0_0_0_2px_rgba(13,148,136,0.1)]'
  },
  forecasts: {
    bg: 'bg-[#EEF2FF]',
    iconBg: 'bg-[#E0E7FF]',
    iconColor: 'text-[#4F46E5]',
    hoverBg: 'hover:bg-[#E0E7FF]',
    hoverBorder: 'hover:border-[#A5B4FC]',
    hoverShadow: 'hover:shadow-[0_0_0_2px_rgba(79,70,229,0.1)]'
  },
  insights: {
    bg: 'bg-[#FFFBEB]',
    iconBg: 'bg-[#FEF3C7]',
    iconColor: 'text-[#D97706]',
    hoverBg: 'hover:bg-[#FEF3C7]',
    hoverBorder: 'hover:border-[#FCD34D]',
    hoverShadow: 'hover:shadow-[0_0_0_2px_rgba(217,119,6,0.1)]'
  }
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const { languageConfig } = useLanguage();

  const loadDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/executive`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboard();
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'CRITICAL': return 'bg-red-500';
      case 'NEEDS_ATTENTION': return 'bg-orange-500';
      case 'STABLE': return 'bg-yellow-500';
      case 'HEALTHY': return 'bg-green-500';
      default: return 'bg-slate-500';
    }
  };

  const getHealthBg = (status) => {
    switch (status) {
      case 'CRITICAL': return 'bg-red-50 border-red-200';
      case 'NEEDS_ATTENTION': return 'bg-orange-50 border-orange-200';
      case 'STABLE': return 'bg-yellow-50 border-yellow-200';
      case 'HEALTHY': return 'bg-green-50 border-green-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Business Intelligence...</p>
        </div>
      </div>
    );
  }

  if (!data?.hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="dashboard-no-data">
        <BarChart3 className="h-16 w-16 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-700">No Business Data Available</h2>
        <p className="text-slate-500 text-center max-w-md">
          Run AI agents to generate business intelligence. Start with data upload and agent execution.
        </p>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/data-upload')}>Upload Data</Button>
          <Button variant="outline" onClick={() => navigate('/agents')}>Run Agents</Button>
        </div>
      </div>
    );
  }

  const health = data.businessHealth || {};
  const revenue = data.revenueMetrics || {};
  const inventory = data.inventoryHealth || {};
  const customers = data.customerMetrics || {};
  const stores = data.storePerformance || {};
  const alerts = data.criticalAlerts || [];
  const recommendations = data.topRecommendations || [];

  // Agent Intelligence Card Component with translation
  const AgentCard = ({ agent, icon: Icon, path, summary, style }) => {
    return (
      <div
        onClick={() => navigate(path)}
        className={`
          ${style.bg} ${style.hoverBorder}
          rounded-xl border border-transparent
          p-5 cursor-pointer group
          transition-all duration-200 ease-out
          hover:shadow-[0_4px_12px_rgba(0,0,0,0.05)]
          hover:-translate-y-0.5
        `}
        data-testid={`agent-card-${agent.toLowerCase()}`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className={`${style.iconBg} p-2.5 rounded-xl`}>
            <Icon className={`h-5 w-5 ${style.iconColor}`} />
          </div>
          <ChevronRight 
            className={`h-5 w-5 text-slate-300 group-hover:text-slate-500 transition-all duration-200 group-hover:translate-x-0.5`} 
          />
        </div>
        <h3 className="font-semibold text-slate-800 text-base mb-1.5">{agent}</h3>
        <p className="text-sm text-slate-500 leading-relaxed line-clamp-2" style={{ fontFamily: languageConfig?.font }}>
          <TranslatedText text={summary} cacheKey={`dashboard:agent:${agent}`} />
        </p>
      </div>
    );
  };

  // Quick Action Tile Component
  const QuickActionTile = ({ icon: Icon, label, path, style, tooltip }) => (
    <button
      onClick={() => navigate(path)}
      className={`
        ${style.bg} ${style.hoverBg} ${style.hoverBorder} ${style.hoverShadow}
        rounded-xl border border-slate-200
        p-4 flex flex-col items-center justify-center gap-2.5
        transition-all duration-200 ease-out
        hover:-translate-y-0.5 hover:shadow-lg
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        group cursor-pointer
        min-h-[90px]
      `}
      title={tooltip}
      data-testid={`quick-action-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className={`${style.iconBg} p-2.5 rounded-lg transition-transform duration-200 group-hover:scale-105`}>
        <Icon className={`h-5 w-5 ${style.iconColor}`} />
      </div>
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </button>
  );

  return (
    <div className="space-y-6 pb-10" data-testid="executive-dashboard">
      {/* Header with Health Status */}
      <div className={`rounded-xl border-2 p-6 ${getHealthBg(health.status)}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full ${getHealthColor(health.status)} flex items-center justify-center`}>
              {health.status === 'HEALTHY' ? (
                <CheckCircle className="h-8 w-8 text-white" />
              ) : health.status === 'CRITICAL' ? (
                <AlertTriangle className="h-8 w-8 text-white" />
              ) : (
                <Activity className="h-8 w-8 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Business Health: {health.status}</h1>
              <p className="text-slate-600" style={{ fontFamily: languageConfig?.font }}>
                <TranslatedText 
                  text={health.criticalAlerts > 0 
                    ? `${health.criticalAlerts} critical alerts require attention`
                    : 'All systems operating normally'} 
                  cacheKey={`dashboard:health:${health.criticalAlerts || 0}`}
                />
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right mr-4">
              <p className="text-xs text-slate-500">Data Coverage</p>
              <p className="font-semibold text-slate-700">{data.agentsCovered}/9 Agents</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => navigate('/executive-summary')}>
              <Sparkles className="h-4 w-4 mr-2" />
              Full Report
            </Button>
          </div>
        </div>
      </div>

      {/* Key Revenue Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <IndianRupee className="h-5 w-5 text-blue-600" />
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-xs text-blue-700 font-medium">Total Revenue</p>
            <p className="text-xl font-bold text-blue-900">{revenue.totalRevenue || 'N/A'}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <ShoppingCart className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-xs text-green-700 font-medium">Transactions</p>
            <p className="text-xl font-bold text-green-900">{revenue.totalTransactions || 'N/A'}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Target className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-xs text-purple-700 font-medium">Avg Transaction</p>
            <p className="text-xl font-bold text-purple-900">{revenue.avgTransactionValue || 'N/A'}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="h-5 w-5 text-indigo-600" />
            </div>
            <p className="text-xs text-indigo-700 font-medium">Customers</p>
            <p className="text-xl font-bold text-indigo-900">{customers.totalCustomers || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Store className="h-5 w-5 text-teal-600" />
            </div>
            <p className="text-xs text-teal-700 font-medium">Active Stores</p>
            <p className="text-xl font-bold text-teal-900">{stores.totalStores || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Package className="h-5 w-5 text-orange-600" />
            </div>
            <p className="text-xs text-orange-700 font-medium">On-Hand Units</p>
            <p className="text-xl font-bold text-orange-900">{inventory.totalOnHand?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Critical Alerts */}
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              Critical Alerts
            </CardTitle>
            <CardDescription>{alerts.length} issues need attention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {alerts.length > 0 ? alerts.slice(0, 4).map((alert, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${
                alert.severity === 'high' ? 'bg-red-100 border-red-300' : 'bg-orange-100 border-orange-300'
              }`}>
                <div className="flex items-start gap-2">
                  <AlertCircle className={`h-4 w-4 mt-0.5 ${
                    alert.severity === 'high' ? 'text-red-600' : 'text-orange-600'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700">{alert.agent}</p>
                    <p className="text-sm text-slate-600 line-clamp-2" style={{ fontFamily: languageConfig?.font }}>
                      <TranslatedText text={alert.message} cacheKey={`dashboard:alert:${idx}`} />
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-4 text-green-600">
                <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">No critical alerts</p>
              </div>
            )}
            {alerts.length > 4 && (
              <Button variant="ghost" size="sm" className="w-full" onClick={() => navigate('/agent-recommendations')}>
                View All {alerts.length} Alerts <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Inventory Health */}
        <Card className="border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
              <Package className="h-5 w-5" />
              Inventory Health
            </CardTitle>
            <CardDescription>Stock status overview</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-xs text-red-700">Stockout Risk</p>
                <p className="text-2xl font-bold text-red-800">{inventory.stockoutRiskSKUs || 0}</p>
                <p className="text-xs text-red-600">SKUs</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-700">Dead Stock</p>
                <p className="text-2xl font-bold text-yellow-800">{inventory.deadStockSKUs || 0}</p>
                <p className="text-xs text-yellow-600">SKUs</p>
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Blocked Capital</span>
                <span className="font-bold text-slate-800">
                  {typeof inventory.blockedCapital === 'number' 
                    ? `₹${(inventory.blockedCapital/100000).toFixed(2)} L`
                    : inventory.blockedCapital || 'N/A'}
                </span>
              </div>
            </div>
            <Button variant="outline" size="sm" className="w-full" onClick={() => navigate('/dashboard/stock-sense')}>
              View StockSense Dashboard <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Top Recommendations */}
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-green-800">
              <Zap className="h-5 w-5" />
              Top Recommendations
            </CardTitle>
            <CardDescription>AI-powered action items with business impact</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {recommendations.length > 0 ? recommendations.slice(0, 4).map((rec, idx) => (
              <div 
                key={idx} 
                className="p-3 bg-white rounded-lg border border-green-200 hover:border-green-400 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => navigate('/agent-recommendations')}
              >
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-green-600 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-green-700">{rec.agent}</p>
                      {rec.impactValue && (
                        <span className="text-xs font-bold text-amber-600">{rec.impactValue}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2" style={{ fontFamily: languageConfig?.font }}>
                      <TranslatedText text={rec.action} cacheKey={`dashboard:rec:${idx}`} />
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-4 text-slate-500">
                <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No recommendations yet</p>
              </div>
            )}
            <Button variant="outline" size="sm" className="w-full text-green-700 border-green-300" onClick={() => navigate('/agent-recommendations')}>
              View Business Action Center <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Agent Intelligence Cards - Premium Styled */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Agent Intelligence</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <AgentCard
            agent="SalesIQ"
            icon={TrendingUp}
            path="/dashboard/sales-iq"
            summary={data.agentSummaries?.SalesIQ || 'Revenue intelligence and sales analytics'}
            style={AGENT_STYLES.SalesIQ}
          />
          <AgentCard
            agent="Customer360"
            icon={Users}
            path="/dashboard/customer-360"
            summary={customers.atRiskCustomers > 0 
              ? `${customers.atRiskCustomers} at-risk customers identified`
              : data.agentSummaries?.Customer360 || 'Customer behavior and RFM analysis'}
            style={AGENT_STYLES.Customer360}
          />
          <AgentCard
            agent="StorePulse"
            icon={Store}
            path="/dashboard/store-pulse"
            summary={stores.underperformingStores > 0 
              ? `${stores.underperformingStores} underperforming stores`
              : data.agentSummaries?.StorePulse || 'Store performance and conversion'}
            style={AGENT_STYLES.StorePulse}
          />
          <AgentCard
            agent="ProductMix"
            icon={PieChart}
            path="/dashboard/product-mix"
            summary={data.agentSummaries?.ProductMix || 'Product lifecycle and category analysis'}
            style={AGENT_STYLES.ProductMix}
          />
        </div>

        {/* Additional Agents Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <AgentCard
            agent="StockSense"
            icon={Boxes}
            path="/dashboard/stock-sense"
            summary={inventory.stockoutRiskSKUs > 0
              ? `${inventory.stockoutRiskSKUs} SKUs at stockout risk`
              : 'Inventory optimization and stock alerts'}
            style={AGENT_STYLES.StockSense}
          />
          <AgentCard
            agent="VendorLink"
            icon={Package}
            path="/dashboard/vendor-link"
            summary={data.agentSummaries?.VendorLink || 'Vendor performance and supply chain'}
            style={AGENT_STYLES.VendorLink}
          />
          <AgentCard
            agent="PromoIQ"
            icon={Target}
            path="/dashboard/promo-iq"
            summary={data.agentSummaries?.PromoIQ || 'Promotion effectiveness analysis'}
            style={AGENT_STYLES.PromoIQ}
          />
          <AgentCard
            agent="DemandFlow"
            icon={LineChart}
            path="/dashboard/demand-flow"
            summary={data.agentSummaries?.DemandFlow || 'Demand sensing and trend prediction'}
            style={AGENT_STYLES.DemandFlow}
          />
        </div>
      </div>

      {/* Quick Actions - Premium Action Tiles */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <QuickActionTile
            icon={Upload}
            label="Upload Data"
            path="/upload-res"
            style={QUICK_ACTION_STYLES.upload}
            tooltip="Upload sales, inventory, and customer data"
          />
          <QuickActionTile
            icon={Activity}
            label="Run Agents"
            path="/agents"
            style={QUICK_ACTION_STYLES.agents}
            tooltip="Run all agents on latest data"
          />
          <QuickActionTile
            icon={Brain}
            label="Executive Brain"
            path="/dashboard/executive-brain"
            style={QUICK_ACTION_STYLES.brain}
            tooltip="AI-powered business intelligence summary"
          />
          <QuickActionTile
            icon={MessageSquare}
            label="Ask ReasonBot"
            path="/chatbot"
            style={QUICK_ACTION_STYLES.chat}
            tooltip="Chat with AI about your business data"
          />
          <QuickActionTile
            icon={LineChart}
            label="Forecasts"
            path="/forecasts"
            style={QUICK_ACTION_STYLES.forecasts}
            tooltip="View sales and demand forecasts"
          />
          <QuickActionTile
            icon={Zap}
            label="All Insights"
            path="/agent-recommendations"
            style={QUICK_ACTION_STYLES.insights}
            tooltip="View all AI-generated recommendations"
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
